import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import isEqual from 'lodash/isEqual';

export const useDebounceState = <T>(
  defaultValue: T,
  preprocess?: (value: T) => T,
  debounceTimeMs = 1000,
): [T, T, (value: T) => void] => {
  const [valueDebounced, setDebouncedValue] = useState<T>(defaultValue);
  const [value, setValue] = useState<T>(defaultValue);

  const timer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }
    };
  }, []);

  const debounceFn = useCallback(
    (value: T) => {
      timer.current = null;
      const nextValue = preprocess ? preprocess(value) : value;
      setDebouncedValue((current) => (isEqual(current, nextValue) ? current : nextValue));
    },
    [preprocess],
  );

  const setDebounce = useCallback(
    (value: T) => {
      setValue(value);
      if (timer.current) {
        window.clearTimeout(timer.current);
      }
      timer.current = window.setTimeout(debounceFn, debounceTimeMs, value);
    },
    [debounceFn, debounceTimeMs],
  );

  return [value, valueDebounced, setDebounce];
};
